"use client";

import React, { useState } from "react";
import { Settings2, Zap, Building2, FileText } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
const specialties = [{
  id: "item-1",
  title: "24/7 Availability & AMC",
  description: "Our Annual Maintenance Contracts ensure your infrastructure never sleeps. We provide round-the-clock monitoring and rapid response to keep your business operations fluid and uninterrupted.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1676583/img/it_masters_server_uptime.png",
  icon: Zap
}, {
  id: "item-2",
  title: "Hospitality VM Infrastructure",
  description: "Specialized virtualization projects for world-leading hotel brands. We architect high-stakes environments that power guest services, back-office security, and seamless property management.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1676583/img/hospitality_virtualization_architecture.png",
  icon: Building2
}, {
  id: "item-3",
  title: "Gold-Standard Cybersecurity",
  description: "Edge-to-edge encryption and proactive threat hunting. We partner with global security leaders to ensure your reputation and data remain untouchable in an evolving digital landscape.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1676583/img/cybersecurity_threat_protection.png",
  icon: Settings2
}, {
  id: "item-4",
  title: "Strategic IT Procurement",
  description: "Leveraging decade-long industry relationships to source high-performance hardware. We optimize your IT spend while ensuring your stack scales with your enterprise ambition.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1676583/img/it_procurement_hardware_optimization.png",
  icon: FileText
}];
const technicalSpecs = [{
  specialtyId: "item-1",
  label: "SLA Guarantee",
  value: "99.99% Uptime"
}, {
  specialtyId: "item-1",
  label: "Response Time",
  value: "< 15 Minutes"
}, {
  specialtyId: "item-1",
  label: "Monitoring",
  value: "Proactive AI-Driven"
}, {
  specialtyId: "item-2",
  label: "VM Platform",
  value: "VMware / Hyper-V"
}, {
  specialtyId: "item-2",
  label: "Guest WiFi",
  value: "High-Density Mesh"
}, {
  specialtyId: "item-2",
  label: "Compliance",
  value: "PCI-DSS / GDPR"
}, {
  specialtyId: "item-3",
  label: "Encryption",
  value: "AES-256 Bit"
}, {
  specialtyId: "item-3",
  label: "Protection",
  value: "Zero-Trust Model"
}, {
  specialtyId: "item-3",
  label: "Auditing",
  value: "Real-time SIEM"
}, {
  specialtyId: "item-4",
  label: "Cost Reduction",
  value: "Up to 30% Annual"
}, {
  specialtyId: "item-4",
  label: "Lifecycle",
  value: "5-Year Roadmap"
}, {
  specialtyId: "item-4",
  label: "Partners",
  value: "Dell, HP, Cisco"
}];
export default function TechnicalExpertise() {
  const [activeImage, setActiveImage] = useState(specialties[0].image);
  return <section data-section-id="224251" id="zero-downtime-operations" className="bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-px bg-border" />
      <div className="absolute top-0 left-1/2 w-px h-full bg-border/50 -translate-x-1/2 hidden lg:block" />

      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-16 max-w-3xl">
          <h2 className="font-default text-4xl md:text-5xl font-bold tracking-tighter text-foreground mb-6 uppercase">
            Zero-Downtime Operations
          </h2>
          <p className="font-serif text-lg text-muted-foreground leading-relaxed">
            We architect resilient, cost-effective infrastructure that scales with your ambition. 
            From high-stakes hospitality virtualization to gold-standard cybersecurity, 
            IT Masters is the silent engine of your digital transformation.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-start relative">
          {}
          <div className="flex flex-col w-full">
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
              {specialties.map((item, i) => {
              const Icon = item.icon;
              const itemSpecs = technicalSpecs.filter(spec => spec.specialtyId === item.id);
              return <div key={item.id} data-index={i} onMouseEnter={() => setActiveImage(item.image)} className="group">
                    <AccordionItem value={item.id} className="border-b-2 border-border py-2 data-[state=open]:border-primary transition-colors">
                      <AccordionTrigger className="hover:no-underline py-6">
                        <div className="flex items-center gap-4 text-left">
                          <div className="p-3 bg-muted/50 rounded-md border border-border group-data-[state=open]:bg-primary group-data-[state=open]:text-primary-foreground transition-colors">
                            <Icon className="w-6 h-6" />
                          </div>
                          <span className="font-default text-xl font-semibold tracking-tight">
                            {item.title}
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pt-2 pb-8">
                        <p className="font-serif text-muted-foreground mb-8 text-base leading-relaxed pl-16">
                          {item.description}
                        </p>
                        
                        <div className="pl-16">
                          <h4 className="font-default font-semibold text-sm uppercase tracking-widest text-secondary mb-4 flex items-center gap-2">
                            <span className="w-2 h-2 bg-secondary block rounded-md" />
                            Hospitality SLA Standards
                          </h4>
                          <div className="border border-border bg-card/50 backdrop-blur-sm rounded-md overflow-hidden shadow-md">
                            <Table>
                              <TableHeader className="bg-muted/30">
                                <TableRow className="hover:bg-transparent border-border">
                                  <TableHead className="font-mono text-xs uppercase tracking-wider text-muted-foreground w-1/2">Parameter</TableHead>
                                  <TableHead className="font-mono text-xs uppercase tracking-wider text-muted-foreground w-1/2">Specification</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {itemSpecs.map((spec, index) => <TableRow key={index} data-index={index} className="border-border hover:bg-muted/20">
                                    <TableCell className="font-serif text-sm font-medium text-foreground">
                                      {spec.label}
                                    </TableCell>
                                    <TableCell className="font-mono text-sm text-primary">
                                      {spec.value}
                                    </TableCell>
                                  </TableRow>)}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </div>;
            })}
            </Accordion>
          </div>

          {}
          <div className="hidden lg:block sticky top-32">
            <div className="relative aspect-square w-full border border-primary bg-muted p-2 shadow-2xl transition-all duration-500 ease-in-out rounded-md">
              {}
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-primary -translate-x-1 -translate-y-1" />
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-primary translate-x-1 -translate-y-1" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-primary -translate-x-1 translate-y-1" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-primary translate-x-1 translate-y-1" />
              
              <div className="w-full h-full relative overflow-hidden bg-background rounded-md">
                <img src={activeImage} alt="Technical Infrastructure Visualization" className="w-full h-full object-cover transition-opacity duration-500 grayscale opacity-80 hover:grayscale-0 hover:opacity-100" />
                
                {}
                <div className="absolute inset-0 pointer-events-none opacity-5" style={{
                backgroundImage: `linear-gradient(to right, var(--color-primary) 1px, transparent 1px), linear-gradient(to bottom, var(--color-primary) 1px, transparent 1px)`,
                backgroundSize: '40px 40px'
              }} />
              </div>
              
              {}
              <div className="absolute bottom-4 right-4 bg-background/90 backdrop-blur-md border border-border p-2 font-mono text-[10px] text-foreground flex flex-col gap-1 rounded-md shadow-lg">
                <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-secondary rounded-md animate-pulse" />SYSTEM.STABLE</span>
                <span className="text-primary">INFRA.ACTIVE.NODE</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}